import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retryWhen, scan, mergeMap } from 'rxjs/operators';
import { retryExclusions } from '@core/constants/exclusion-url';

/**
 * An HTTP interceptor that retries failed HTTP requests up to a specified number of times.
 * This interceptor is useful for handling transient errors such as network issues.
 *
 * @class RetryInterceptor
 * @implements {HttpInterceptor}
 */
@Injectable()
export class RetryInterceptor implements HttpInterceptor {
  private readonly MAX_RETRIES = 3; // Maximum number of retries
  private readonly DEFAULT_RETRY_DELAY = 1000; // 1 second by default
  private readonly MAX_RETRY_DELAY = 30000; // Max 30 seconds

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // if the request URL matches any of the exclusion patterns, skip retry logic
    if (this.isExcludedFromRetry(req?.url)) {
      return next.handle(req);
    }
    return next.handle(req).pipe(
      retryWhen((errors) =>
        errors.pipe(
          scan(
            (acc, error) => {
              if (acc.retryCount < this.MAX_RETRIES && error instanceof HttpErrorResponse) {
                // Retry only for 500 and 429 errors
                if (error.status === 500 || error.status === 429) {
                  return { retryCount: acc.retryCount + 1, error };
                }
              }
              throw error;
            },
            { retryCount: 0, error: null as HttpErrorResponse | null },
          ),
          mergeMap(({ retryCount, error }) => {
            const delay = this.calculateRetryDelay(error, retryCount);
            console.warn(`Retrying request (attempt ${retryCount}/${this.MAX_RETRIES}) after ${delay}ms delay`);
            return timer(delay);
          }),
        ),
      ),
      catchError((error) => {
        console.error('Request failed after retries:', error);
        return throwError(error);
      }),
    );
  }

  /**
   * Calculate the appropriate delay based on the error and retry count
   * @param error The HttpErrorResponse received
   * @param retryCount The current retry attempt count
   * @returns The delay in milliseconds before the next retry
   */
  private calculateRetryDelay(error: HttpErrorResponse, retryCount: number): number {
    // If response contains Retry-After header, use it with priority
    if (error?.headers?.has('Retry-After')) {
      const retryAfter = error.headers.get('Retry-After');
      if (retryAfter) {
        const delay = this.parseRetryAfter(retryAfter);
        if (delay > 0) {
          return Math.min(delay, this.MAX_RETRY_DELAY);
        }
      }
    }

    // Exponential backoff for other cases
    const exponentialDelay = this.DEFAULT_RETRY_DELAY * Math.pow(2, retryCount - 1);
    return Math.min(exponentialDelay, this.MAX_RETRY_DELAY);
  }

  /**
   * Parse the Retry-After header which can be in seconds or HTTP date format
   * @param retryAfter The value of the Retry-After header
   * @returns The delay in milliseconds before retrying
   */
  private parseRetryAfter(retryAfter: string): number {
    // Si es un número (segundos)
    const seconds = parseInt(retryAfter, 10);
    if (!isNaN(seconds)) {
      return seconds * 1000; // Convert to milliseconds
    }

    // If it's an HTTP date
    try {
      const retryDate = new Date(retryAfter);
      const now = new Date();
      const delay = retryDate.getTime() - now.getTime();
      return delay > 0 ? delay : 0;
    } catch {
      return 0;
    }
  }

  /**
   * Determines if the URL should be excluded from retry
   * @param url request URL to check against the exclusion list
   * @returns true if the URL matches any exclusion pattern, false otherwise
   */
  private isExcludedFromRetry(url: string): boolean {
    if (!url) {
      return false;
    }
    return retryExclusions.some((pattern) => {
      if (typeof pattern === 'string') {
        return url.includes(pattern);
      } else if (pattern instanceof RegExp) {
        return pattern.test(url);
      }
      return false;
    });
  }
}
